import {BaseStore, globalStore, StoreObject} from "../../stem-core/state/Store";
import {Money} from "../../stem-core/localization/Money";
import {apiClient} from "../connection/BlinkApiClient";
import {StemDate} from "../../stem-core/time/Date";
import {Currency} from "../../stem-core/localization/CurrencyStore";
import {Merchant} from "./MerchantStore";
import {field} from "../../stem-core/state/StoreField";
import {CycleUnit} from "./RecurringPaymentStoreObject";
import {EditableObjectStatus, InputFieldRequiredType, VisibilityStatus} from "./misc/GenericEnums";
import {BlinkGlobal} from "../../blinkpay/UtilsLib";
import {filterStoreByMerchantAndLooseIdArray} from "./misc/Filter";
import {ShippingPriceEntry} from "./misc/ShippingPrice";
import {SubscriptionCoverage} from "./SubscriptionCoverageStore";


@globalStore
export class BillingPlan extends BaseStore("BillingPlan", {dependencies: ["Currency", "Merchant"]}) {
    @field(Number) merchantId: number;
    @field("Merchant") merchant: Merchant;
    @field(Number) currencyId: number;
    @field("Currency") currency: Currency;
    @field(Number) maxPaymentAttemptsCount: number;
    @field(Number) paymentAttemptDelayHours: number;
    @field(Number) attemptsBeforeNextCycle: number;
    @field(Number) useCalendarPeriod: number;
    @field(Boolean) collectAddress: boolean;
    @field(Number) amount: number;
    @field(CycleUnit) cycleUnit: CycleUnit;
    @field(Number) cycleUnitCount: number;
    @field(Object) shippingPrices: any[]; // An array of entries, of which the first matching one is used
    @field(Number) minNumberOfCycles: number;
    @field(Number) maxNumberOfCycles: number;
    @field(Boolean) autorenewEditable: boolean;
    @field(Boolean) autorenewDefault: boolean;
    @field(Boolean) collectBillingAddress: boolean;
    @field(Number) freeTrialDays: number;
    @field(InputFieldRequiredType) trialPaymentMethodRequired: InputFieldRequiredType;
    @field(EditableObjectStatus) status: EditableObjectStatus;
    @field(Array) extraBenefitIds: number[];

    // TODO @Mihai does this method really need to exist?
    hasShippingPrice(): boolean {
        // TODO: this is a strange method
        return Array.isArray(this.shippingPrices) && this.shippingPrices.length > 0;
    }

    // TODO @branch cleanup and merge with Product
    // Return an array of ShippingPrice objects. Will modify the objects inline.
    getShippingPrices(): ShippingPriceEntry[] {
        return ShippingPriceEntry.loadArray(this.shippingPrices, this.currencyId);
    }

    getAbbreviatedCycleDuration() {
        const cycleUnitAbrev = this.cycleUnit.getAbrev();
        return this.cycleUnitCount > 1 ? this.cycleUnitCount + " " + cycleUnitAbrev : cycleUnitAbrev;
    }

    getCycleDuration(omitCountOnSingular=false) {
        return this.cycleUnit.timeUnit.formatCount(this.cycleUnitCount, omitCountOnSingular);
    }

    getCurrency() {
        return this.currency;
    }

    formatPrice(amount: number): Money {
        return new Money(amount, this.getCurrency());
    }

    // TODO @branch Replace usage with getBasePrice
    getPrice(): Money {
        return this.formatPrice(this.amount);
    }

    getBasePrice() {
        return this.formatPrice(this.amount);
    }

    formatBasePrice() {
        return `${this.getBasePrice()}/${this.getCycleDuration(true)}`;
    }

    serializeForSDK() {
        const {amount, collectBillingAddress, currencyId, cycleUnit, cycleUnitCount, id, merchantId, shippingPrices, status} = this;
        return {amount, collectBillingAddress, currencyId, cycleUnit, cycleUnitCount, id, merchantId, shippingPrices, status};
    }
}


@globalStore
export class SubscriptionOffer extends BaseStore("SubscriptionOffer", {dependencies: ["SubscriptionDiscountCode", "SubscriptionCoverage", "BillingPlan"]}) {
    @field("Merchant") merchant: Merchant;
    @field("BillingPlan") billingPlan: BillingPlan;
    @field(Number) coverageId: number;
    @field("SubscriptionCoverage") coverage: SubscriptionCoverage;
    @field(VisibilityStatus) visibility: VisibilityStatus;
    @field(Array) extraBenefitIds: number[];
    @field(Boolean) collectAddress: boolean;
    @field(Date) startDate: StemDate;
    @field(Date) endDate: StemDate;
    @field(Date) deletedAt: StemDate;
    @field(Boolean) revoked: boolean;
    @field(String) title?: string;
    @field(String) description?: string

    toString(): string {
        return `${this.getName()} ${this.formatBasePrice()}`
    }

    getCoverage() {
        return this.coverage;
    }

    shouldCollectAddress() {
        return this.coverage.requiresAddress || this.collectAddress;
    }

    getMerchant() {
        return this.merchant;
    }

    hasShippingPrice() {
        return this.billingPlan.hasShippingPrice();
    }

    // Return an array of ShippingPrice objects. Will modify the objects inline.
    getShippingPrices() {
        return this.billingPlan.getShippingPrices();
    }

    getName() {
        return this.title || this.getCoverage()?.getName();
    }

    getAbbreviatedCycleDuration() {
        return this.billingPlan.getAbbreviatedCycleDuration();
    }

    getCycleDuration(omitCountOnSingular=false) {
        return this.billingPlan.getCycleDuration(omitCountOnSingular);
    }

    getCurrency() {
        return this.billingPlan.currency;
    }

    formatPrice(amount: number) {
        return this.billingPlan.formatPrice(amount);
    }

    // TODO @branch Go through usage of this and basePrice, and make sure they are correctly used
    // TODO: this should be called get localized price?
    getPrice() {
        // TODO: if pricing should include shipping should be a merchant setting
        const defaultPrice = this.billingPlan.getPrice();
        const defaultLocation = BlinkGlobal.userService?.userData?.location;
        if (!defaultLocation) {
            return defaultPrice;
        }

        const shippingPrices = this.getShippingPrices();

        for (const shippingPrice of shippingPrices) {
            if (shippingPrice.matchesAddress(defaultLocation)) {
                return defaultPrice.add(shippingPrice.getPrice());
            }
        }

        return defaultPrice;
    }

    getBasePrice() {
        return this.billingPlan.getBasePrice();
    }

    formatBasePrice() {
        return this.billingPlan.formatBasePrice();
    }

    getDescription() {
        return this.description || "";
    }

    getAllBenefitIds() {
        const benefitIds = new Set([...this.extraBenefitIds, ...this.coverage.benefitIds]);
        return Array.from(benefitIds);
    }

    // TODO @branch maybe rename to isActive?
    isAvailable() {
        return !this.revoked && !this.deletedAt && StemDate.now().getTime() >= (new StemDate(this.startDate)).getTime()
            && (!this.endDate || StemDate.now().getTime() <= (new StemDate(this.endDate)).getTime());
    }

    static allUnfiltered() {
        // Because the IFrameState is able to overwrite the set of subscription offers, we overwrite the .all() method
        // on this store to take that into account. We need this method to be able to implement the filter in
        // IFrameState.
        // Order the offers decreasingly by start date.
        return super.all<SubscriptionOffer>().sort((subA, subB) => new Date(subB.startDate).getTime() - new Date(subA.startDate).getTime());
    }

    // TODO @cleanup rewrite this
    static all() {
        return filterStoreByMerchantAndLooseIdArray(this.allUnfiltered(), BlinkGlobal.iFrameState?.subscriptionOffer,
            (offer, idOrAlias) => offer.id == idOrAlias || offer.alias == idOrAlias);
    }
}

// TODO @cleanup these endpoints should be merged
export async function apiCalculateSubscriptionOffersPrice(calculatePriceRequest = {
    discountCode: null,
    merchantId: null,
    deliveryAddressId: null,
}) {
    return apiClient.get("/subscriptions/calculate_subscription_price", calculatePriceRequest);
}

export async function apiCalculateSubscriptionEditOffersPrice(calculatePriceRequest={
    subscriptionId: null,
    discountCode: null,
    deliveryAddressId: null,
}) {
    return apiClient.get("/subscriptions/calculate_update_subscription_price", calculatePriceRequest);
}

export async function apiMerchantCreateSubscriptionOffer(request: any): Promise<SubscriptionOffer> {
    const response = await apiClient.post("/merchant/create_subscription_offer", request);
    return SubscriptionOffer.loadObject(response);
}

export async function apiMerchantEditSubscriptionOffer(request: any): Promise<any> {
    return apiClient.post("/merchant/edit_subscription_offer", request);
}

export async function apiMerchantDeleteSubscriptionOffer(request: any): Promise<any> {
    return apiClient.post("/merchant/delete_subscription_offer", request);
}

export async function apiMerchantCreateBillingPlan(request: any): Promise<BillingPlan> {
    const response = await apiClient.post("/merchant/create_billing_plan", request);
    return BillingPlan.loadObject(response);
}

export function apiMerchantEditBillingPlan(request: any): Promise<any> {
    return apiClient.post("/merchant/edit_billing_plan", request);
}
